import * as React from 'react';
import debounce from 'debounce';

import { AutocompletePrediction, PredictionType } from 'ha/types/GoogleMaps';
import { RenderInputFn } from 'ha/ui/next/Autocomplete/Autocomplete';
import LocationAutocompleteInput, {
  SimpleItem,
} from 'ha/components/next/LocationAutocompleteInput';

interface RequiredProps {
  // loadSuggestion service
  loadSuggestions: (value: string) => Promise<AutocompletePrediction[]>;
  // event called when the input changed
  onChange: (value: string) => void;
  // event called when an item from the list is selected
  onSelect: (value: string, item: SimpleItem) => void;
  // type of prediction made by google service
  predictionTypes: PredictionType[];
  // the input we want to render in order to search for cities
  renderInput: RenderInputFn;
  // Control when render inital items and when fethced ones
  renderInitialItems?: boolean;
  // list of initial items
  initialItems?: SimpleItem[];
}

interface DefaultProps {
  value?: string;
}

type Props = RequiredProps & DefaultProps;

const initialState = { items: [] as SimpleItem[] };
type State = typeof initialState;

const getItemsFromPredictions = (p: AutocompletePrediction[]): SimpleItem[] =>
  p.map(({ description }, index) => ({
    id: index.toString(),
    label: description,
  }));

export const MIN_SEARCH_INPUT_LENGTH = 1;

class LocationsContainer extends React.Component<Props, State> {
  static defaultProps: DefaultProps = { value: '' };

  state = initialState;

  loadItemsDebounced = debounce((value: string) => {
    if (value && value.length > MIN_SEARCH_INPUT_LENGTH) {
      this.props
        .loadSuggestions(value)
        .then(result => {
          this.setState({
            items: getItemsFromPredictions(result),
          });
        })
        .catch(() => this.setState({ items: [] }));
    }
  }, 300);

  handleChange = (value: string) => {
    this.props.onChange(value);
    if (!value || !value.length) {
      // if user cleared the input then we should clean up the suggestions
      this.setState({ items: [] });
      return;
    }
    this.loadItemsDebounced(value);
  };

  handleSelect = (value: string, item: SimpleItem) => {
    this.setState({
      items: [],
    });
    this.props.onSelect(value, item);
  };

  render() {
    const { items } = this.state;
    const { renderInput, value, renderInitialItems, initialItems } = this.props;

    return (
      <LocationAutocompleteInput
        value={value}
        onChange={this.handleChange}
        onSelect={this.handleSelect}
        items={renderInitialItems && initialItems ? initialItems : items}
        renderInput={renderInput}
      />
    );
  }
}

export default LocationsContainer;
